import { type ReactElement, type ReactNode } from 'react';
import { getContextMessage } from '@/shared/copy/context';
import { getErrorCopy } from '@/shared/copy/errors';
import { getLoadingMessage } from '@/shared/copy/loading';
import { isNotFoundError } from '@/shared/data/errors';
import { useForcedLoadingState } from '@/shared/hooks/useForcedLoadingState';
import { raisedPanelSx } from '@/shared/ui/panelShadowStyles';
import { layoutMeasures } from '@/theme/layoutMeasures';
import { s } from '@/theme/tokens';
import { Section } from '@/ui/layout/Section';
import { Stack } from '@/ui/layout/Stack';
import { Box, PageShell, Surface } from '@/ui/primitives';
import { Button } from '@/ui/primitives/Button';
import { DataGridEmptyState } from '@/ui/primitives/DataGrid';
import { EmptyState } from '../EmptyState';
import { ErrorStatePanel } from '../ErrorStatePanel';
import { LoadingSpinnerPageState } from '../LoadingSpinnerPageState';
import { TableStateShell } from '../TableStateShell';
import type { ErrorInput } from '@/shared/data/errors';
import type { UiTone } from '@/theme/uiTokens';

type PageStateScaffoldProps = {
  session: object | null;
  sessionMessage: string;
  organisationId?: string | null;
  requireOrganisation?: boolean;
  organisationMessage?: string;
  projectId?: string | null;
  requireProject?: boolean;
  projectMessage?: string;
  requireAdmin?: boolean;
  isAdmin?: boolean;
  adminMessage?: string;
  adminFallback?: ReactNode;
  redirecting?: boolean;
  redirectMessage?: string;
  isLoading?: boolean;
  loadingMessage?: string;
  loadingFallback?: ReactNode;
  isError?: boolean;
  error?: ErrorInput;
  errorMessage?: string;
  empty?: boolean;
  emptyMessage?: string;
  emptyTone?: UiTone;
  variant?: 'table' | 'card';
  rows?: number;
  children: ReactNode;
};

type PageStateVariant = NonNullable<PageStateScaffoldProps['variant']>;

type PageStatePanelProps = {
  body?: string;
  title: string;
  variant: PageStateVariant;
};

type PageDataStateProps = {
  action?: ReactNode;
  body?: string;
  title: string;
  variant: PageStateVariant;
};

const PageStatePanel = ({ body, title, variant }: PageStatePanelProps): ReactElement => {
  if (variant === 'table') {
    return (
      <TableStateShell>
        <EmptyState title={title} body={body} surface={false} />
      </TableStateShell>
    );
  }

  return (
    <PageShell maxWidth="xs" paddingX="lg" paddingY="xl" paddingBottom="xl">
      <Stack gap="xl" sx={{ pt: s('5xl') }}>
        <Section>
          <Box sx={{ width: '100%' }}>
            <ErrorStatePanel title={title} body={body} sx={raisedPanelSx} />
          </Box>
        </Section>
      </Stack>
    </PageShell>
  );
};

const PageLoadingState = ({ message }: { message: string }): ReactElement => (
  <LoadingSpinnerPageState message={message} />
);

const PageDataState = ({ action, body, title, variant }: PageDataStateProps): ReactElement => {
  if (variant === 'table') {
    return (
      <TableStateShell
        state="error"
        errorTitle={title}
        errorBody={body ?? ''}
        errorAction={action}
      />
    );
  }

  return (
    <Box
      sx={{
        minHeight: layoutMeasures.viewport.contentMinHeight,
        display: 'grid',
        placeItems: 'center',
        width: '100%',
      }}
    >
      <PageShell maxWidth={false} paddingX="lg" paddingY="lg" sx={{ width: '100%' }}>
        <Surface
          padding="md"
          sx={{ width: '100%', maxWidth: layoutMeasures.statePanel.cardMaxWidth, marginX: 'auto' }}
        >
          <DataGridEmptyState title={title} body={body} action={action} />
        </Surface>
      </PageShell>
    </Box>
  );
};

const reloadPage = (): void => {
  window.location.reload();
};

const PageErrorState = ({
  error,
  errorMessage,
  variant,
}: {
  error?: ErrorInput;
  errorMessage?: string;
  variant: PageStateVariant;
}): ReactElement => {
  const typedError = error as { message?: string } | null | undefined;
  const message = typedError?.message ?? errorMessage ?? getErrorCopy('banner.unexpectedError');

  if (isNotFoundError(error)) {
    return (
      <PageDataState
        variant={variant}
        title={message}
        body="This page may have been removed, or you may no longer have access to it."
      />
    );
  }

  return (
    <PageDataState
      variant={variant}
      title={message}
      action={
        <Button variant="secondary" onClick={reloadPage}>
          Reload
        </Button>
      }
    />
  );
};

export const PageStateScaffold = ({
  session,
  sessionMessage,
  organisationId = null,
  requireOrganisation = false,
  organisationMessage,
  projectId = null,
  requireProject = false,
  projectMessage,
  requireAdmin = false,
  isAdmin = false,
  adminMessage,
  adminFallback,
  redirecting = false,
  redirectMessage,
  isLoading = false,
  loadingMessage,
  loadingFallback,
  isError = false,
  error,
  errorMessage,
  empty = false,
  emptyMessage,
  variant = 'card',
  rows: _rows,
  children,
}: PageStateScaffoldProps): ReactElement => {
  const showForcedLoadingState = useForcedLoadingState();
  const resolvedIsLoading = isLoading || showForcedLoadingState;

  if (!session) {
    return <PageStatePanel variant={variant} title={sessionMessage} />;
  }

  if (requireOrganisation && !organisationId) {
    return (
      <PageStatePanel
        variant={variant}
        title={organisationMessage ?? getContextMessage('selection.workspace.continue')}
      />
    );
  }

  if (requireProject && !projectId) {
    return (
      <PageStatePanel
        variant={variant}
        title={projectMessage ?? getContextMessage('selection.project.continue')}
      />
    );
  }

  if (resolvedIsLoading) {
    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }
    return <PageLoadingState message={loadingMessage ?? getLoadingMessage('page')} />;
  }

  if (isError) {
    return <PageErrorState variant={variant} error={error} errorMessage={errorMessage} />;
  }

  if (requireAdmin && !isAdmin) {
    if (adminFallback) {
      return <>{adminFallback}</>;
    }
    return <PageStatePanel variant={variant} title={adminMessage ?? 'Admin access is required.'} />;
  }

  if (redirecting) {
    return <PageLoadingState message={redirectMessage ?? 'Redirecting...'} />;
  }

  if (empty) {
    return <PageStatePanel variant={variant} title={emptyMessage ?? 'Nothing here yet.'} />;
  }

  return <>{children}</>;
};
